import React, { useMemo } from 'react';

// Standard Code 128 bar patterns (Widths of alternating bar, space, bar, space, bar, space)
const CODE128_PATTERNS: string[] = [
    "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213", // 0-9
    "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132", // 10-19
    "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211", // 20-29
    "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313", // 30-39
    "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331", // 40-49
    "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111", // 50-59
    "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214", // 60-69
    "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111", // 70-79
    "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141", // 80-89
    "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141", // 90-99
    "114131", "311141", "411131", "211412", "211214", "211232", "2331112" // 100-106 (104=StartB, 106=Stop)
];

const START_B_INDEX = 104;
const STOP_INDEX = 106;

interface Code128BarcodeProps {
    value: string;
    width?: number;       // Base module scale
    height?: number;      // Bar height in SVG units
    showText?: boolean;   // Show human-readable text below barcode
    fontSize?: number;
    className?: string;
}

export const Code128Barcode: React.FC<Code128BarcodeProps> = ({
    value,
    width = 1.0,
    height = 40,
    showText = true,
    fontSize = 12,
    className = ''
}) => {
    const { bars, totalModules } = useMemo(() => {
        if (!value || typeof value !== 'string') {
            return { bars: [], totalModules: 0 };
        }

        // Convert string to Code 128 Subset B values (ASCII 32 to 127 -> 0 to 95)
        const codes: number[] = [START_B_INDEX];
        let checksum = START_B_INDEX;

        for (let i = 0; i < value.length; i++) {
            const charCode = value.charCodeAt(i);
            const codeVal = (charCode >= 32 && charCode <= 126) ? charCode - 32 : 0;
            codes.push(codeVal);
            checksum += codeVal * (i + 1);
        }

        codes.push(checksum % 103);
        codes.push(STOP_INDEX);

        // Build list of bar/space modules with safe 5-module quiet zone
        const calculatedBars: { x: number; w: number }[] = [];
        let currentPos = 5; // Quiet zone left (5 modules)

        for (const code of codes) {
            const pattern = CODE128_PATTERNS[code];
            if (!pattern) continue;

            for (let p = 0; p < pattern.length; p++) {
                const moduleCount = parseInt(pattern[p], 10);
                const isBar = p % 2 === 0;

                if (isBar) {
                    calculatedBars.push({
                        x: currentPos * width,
                        w: moduleCount * width
                    });
                }
                currentPos += moduleCount;
            }
        }

        currentPos += 5; // Quiet zone right (5 modules)

        return {
            bars: calculatedBars,
            totalModules: currentPos * width
        };
    }, [value, width]);

    if (!value) {
        return null;
    }

    const svgWidth = Math.max(1, totalModules);
    const svgHeight = height + (showText ? fontSize + 8 : 4);

    return (
        <div
            className={`barcode-container ${className}`}
            style={{
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}
        >
            <svg
                width="100%"
                height="auto"
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                preserveAspectRatio="xMidYMid meet"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block'
                }}
                data-testid="code128-svg"
            >
                <rect width={svgWidth} height={svgHeight} fill="#FFFFFF" />
                {bars.map((bar, idx) => (
                    <rect
                        key={idx}
                        x={bar.x}
                        y={2}
                        width={bar.w}
                        height={height}
                        fill="#000000"
                    />
                ))}
                {showText && (
                    <text
                        x={svgWidth / 2}
                        y={height + fontSize + 3}
                        textAnchor="middle"
                        fontFamily="monospace, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto"
                        fontSize={fontSize}
                        fontWeight="700"
                        letterSpacing="1px"
                        fill="#000000"
                        data-testid="barcode-text"
                    >
                        {value}
                    </text>
                )}
            </svg>
        </div>
    );
};

export default Code128Barcode;
